import {
  Card,
  CardHeader,
  SvgIcon,
  CardContent,
  Button,
  Tooltip,
} from '@mui/material';
import {
  RiErrorWarningLine,
  RiArrowRightLine,
  RiEmotionLaughFill,
} from '@remixicon/react';
import { MouseEventHandler, ReactNode } from 'react';
import { useLanguage } from '../../../contexts/language';
import { Dayjs } from 'dayjs';
import {
  DAYJS_VISUALIZATION_DATE_FORMAT,
  DAYJS_VISUALIZATION_TIME_FORMAT,
} from '../../../utils/date';
import {
  createFilterSearchParams,
  FilterDefinition,
} from '../../../hooks/useSearchParamsFilters';
import { useNavigate } from 'react-router';

export const DashboardCard = ({
  icon,
  items,
  title,
  className,
  actionLabel,
  actionUrl,
  actionUrlFilters,
  onActionClick,
  lastUpdate,
}: {
  title: string;
  icon: ReactNode;
  className?: string;
  items: Array<{
    label: string;
    amount: number;
    warning?: boolean;
    helperContent?: ReactNode;
  }>;
  actionLabel?: string;
  actionUrl?: string;
  actionUrlFilters?: FilterDefinition[];
  onActionClick?: MouseEventHandler;
  lastUpdate?: Dayjs;
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [lastUpdateLabel, dateLabel, timeLabel] = lastUpdate?.isValid()
    ? [
        `${t('content.last-update')}:`,
        lastUpdate.format(DAYJS_VISUALIZATION_DATE_FORMAT),
        lastUpdate.format(DAYJS_VISUALIZATION_TIME_FORMAT),
      ]
    : ([] as string[]);

  const url = actionUrl
    ? [
        actionUrl,
        actionUrlFilters?.length
          ? createFilterSearchParams(actionUrlFilters).toString()
          : '',
      ]
        .filter(Boolean)
        .join('?')
    : undefined;

  return (
    <Card
      variant="outlined"
      className={['grid grid-rows-[min-content_1fr]', className]
        .filter(Boolean)
        .join(' ')}
    >
      <CardHeader
        avatar={icon}
        title={
          <div className="grid grid-cols-[1fr_min-content] items-center">
            <span
              title={title}
              className="whitespace-nowrap overflow-hidden text-ellipsis"
            >
              {title}
            </span>
            {lastUpdateLabel && (
              <span
                className="italic text-[10px]/[12px] text-right"
                title={[lastUpdateLabel, dateLabel, timeLabel].join(' ')}
              >
                <span className="whitespace-nowrap">{lastUpdateLabel}</span>{' '}
                <span className="whitespace-nowrap">
                  {`${dateLabel} ${timeLabel}`}
                </span>
              </span>
            )}
          </div>
        }
        className="border-b"
        slotProps={{
          title: {
            className: 'text-lg',
          },
        }}
      />
      <CardContent className="flex flex-wrap gap-2 justify-end">
        <ul className="flex-[1_0_auto]">
          {items.length > 0 ? (
            items.map(({ amount, label, warning, helperContent }) => {
              const content = (
                <div className="flex items-center gap-2">
                  <span>{`${label}: ${amount}`}</span>
                  {warning && (
                    <SvgIcon
                      fontSize="small"
                      color="warning"
                      component={RiErrorWarningLine}
                    />
                  )}
                </div>
              );
              return (
                <li key={label}>
                  {helperContent ? (
                    <Tooltip title={helperContent}>
                      <div className="cursor-help">{content}</div>
                    </Tooltip>
                  ) : (
                    content
                  )}
                </li>
              );
            })
          ) : (
            <li className="flex items-center gap-2">
              {t('content.no-pending-tasks-message')}
              <SvgIcon
                fontSize="small"
                color="success"
                component={RiEmotionLaughFill}
              />
            </li>
          )}
        </ul>
        <div className="self-end justify-self-end">
          <Button
            endIcon={<SvgIcon component={RiArrowRightLine} />}
            variant="outlined"
            size="small"
            onClick={event => {
              onActionClick?.(event);
              if (url && !event.defaultPrevented) {
                void navigate(url);
              }
            }}
          >
            {actionLabel ||
              (actionUrlFilters?.length
                ? t('content.review')
                : t('content.see-all'))}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
